<template>
  <control
    :label="$t('post.code')"
    @clear="handleClear"
    :showClear="savedPostCode"
    :warning="warning"
  >
    <div class="postcode-picker">
      <div class="input-container">
        <a-input
          :value="postCode"
          @change="handleChange"
          @focus="handleFocus"
          @blur="handleBlur"
          class="input"
          :class="{ 'input-error': error }"
        />
        <span v-if="!error && validated" class="message">{{ $t('correct.post.code') }}</span>
        <span v-if="loading" class="validating">{{ `${$t('validating')}...` }}</span>
        <span v-if="error" class="error">{{ $t('invalid.postcode') }}</span>
      </div>
      <question-mark-popover
        class="popover"
        placement="bottomLeft"
        :message="$t('post.code.popover')"
      />
    </div>
  </control>
</template>

<script>
import debounce from 'lodash.debounce';
import { mapActions, mapGetters } from 'vuex';
import { CHECK_POST_CODE } from '@/store/targetingInfo/actionTypes';
import { SECTION_CODES } from '@/constants';
import updateMixin from '@/components/account/targetingInfo/updateMixin';
import Control from '@/components/account/targetingInfo/Control.vue';
import QuestionMarkPopover from '@/components/common/QuestionMarkPopover.vue';

export default {
  mixins: [updateMixin(SECTION_CODES.POST_CODE)],
  components: {
    Control,
    QuestionMarkPopover,
  },
  data() {
    return {
      postCode: '',
      loading: false,
      isFocused: false,
      error: false,
      validated: false,
    };
  },
  computed: {
    ...mapGetters(['targetingInfoData']),
    savedPostCode() {
      return this.targetingInfoData[SECTION_CODES.POST_CODE];
    },
  },
  watch: {
    savedPostCode() {
      this.postCode = this.savedPostCode;
    },
  },
  methods: {
    ...mapActions({
      checkPostCode: CHECK_POST_CODE,
    }),
    handleFocus() {
      this.isFocused = true;
    },
    handleChange(e) {
      this.postCode = e.target.value;
      this.validated = false;
      this.validatePostCode();
    },
    validatePostCode: debounce(async function debouncedValidatePostCode() {
      if (!this.isFocused || !this.postCode) {
        return;
      }

      try {
        this.loading = true;
        this.error = false;
        await this.checkPostCode(this.postCode.trim());
        if (this.isFocused) {
          this.validated = true;
        }
      } catch {
        if (!this.isFocused) {
          return;
        }
        this.error = true;
      } finally {
        this.loading = false;
      }
    }, 1000),
    async handleBlur() {
      if (
        (this.validated && !this.error && this.postCode !== this.savedPostCode) ||
        !this.postCode
      ) {
        await this.onValueChange((this.postCode || '').trim());
      }
      this.postCode = this.savedPostCode;
      this.isFocused = false;
      this.validated = false;
      this.loading = false;
      this.error = false;
    },
    handleClear() {
      this.onValueChange('');
    },
  },
  created() {
    this.postCode = this.savedPostCode;
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/colors';
@import '~@/assets/styles/breakpoints';

.postcode-picker {
  display: flex;
}
.popover {
  margin: 5px 0 0 12px;
}
.input-container {
  width: 292px;
}
.input {
  margin-right: 12px;
  box-shadow: none;
}
.input-error {
  border: 1px solid $red;
}
.validating {
  color: $grey;
}
.message {
  color: $blue;
}
.error {
  color: $red;
}

@media (max-width: $page-mobile-large-breakpoint) {
  .input-container {
    width: 200px;
  }
}
</style>
